import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Moon } from "lucide-react";
import { api, useAuth, withRefresh } from "@/lib/auth";
import { Reveal } from "@/components/temple/Reveal";

const Rich = ({ text, name }) =>
  (text ?? "").replaceAll("{name}", name).split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2 ? <em key={i} className="text-[#D9B47A]">{part.slice(1, -1)}</em> : <Fragment key={i}>{part}</Fragment>,
  );

const Paras = ({ text, name, className }) =>
  (text ?? "").split(/\n\s*\n/).filter((p) => p.trim()).map((p, i) => (
    <p key={i} className={className}>
      {p.split("\n").map((line, j) => <Fragment key={j}>{j > 0 && <br />}<Rich text={line} name={name} /></Fragment>)}
    </p>
  ));

const BlessingCard = ({ b, name, k, signoff }) => (
  <Reveal y={24}>
    <article data-testid={`archive-blessing-${b.id}`} className="border-t border-[#D9B47A]/20 pt-12 pb-16">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">
        {String(k).padStart(2, "0")} · {new Date(b.sent_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
      </p>
      <h2 className="mt-5 font-display text-3xl sm:text-5xl leading-[1.1]"><Paras text={b.heading} name={name} className="" /></h2>
      <div className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed text-[#DCCFC3] max-w-2xl"><Paras text={b.body} name={name} /></div>
      {b.quote?.trim() && <blockquote className="mt-8 border-l-2 border-[#C2414F] pl-5 font-display italic text-2xl text-[#D9B47A] max-w-2xl"><Rich text={b.quote} name={name} /></blockquote>}
      <div className="mt-8 space-y-4 text-base sm:text-lg text-[#DCCFC3] max-w-2xl"><Paras text={b.closing} name={name} /></div>
      {signoff?.name && <p data-testid={`archive-signoff-${b.id}`} className="mt-6 font-display italic text-xl text-[#D9B47A]">{signoff.line}<br />{signoff.name}</p>}
    </article>
  </Reveal>
);

export default function Blessings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!user?.initiate) return undefined;
    let alive = true;
    withRefresh(() => api.get("/blessings"))
      .then(({ data: d }) => alive && setData(d ?? { blessings: [] }))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [user?.initiate]);

  if (user === null) return <main className="min-h-screen" />;
  if (!user?.initiate) {
    return (
      <main data-testid="blessings-locked" className="min-h-[80vh] flex items-center justify-center px-5 pt-28 text-center">
        <div>
          <Moon className="w-10 h-10 mx-auto text-[#D9B47A]" />
          <h1 className="mt-6 font-display text-4xl sm:text-5xl">The blessing archive is kept for initiates.</h1>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/signin" data-testid="blessings-signin" className="rounded-full border border-[#D9B47A]/50 px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Sign in</Link>
            <Link to="/initiate" className="rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em]">Become an initiate</Link>
          </div>
        </div>
      </main>
    );
  }
  const list = Array.isArray(data?.blessings) ? data.blessings : [];
  const name = data?.name ?? "beloved";
  return (
    <main data-testid="blessings-page" className="max-w-[1100px] mx-auto px-5 sm:px-10 pt-36 pb-24">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">For initiates · The blessing archive</p>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">Every blessing, <em className="text-[#D9B47A]">kept for you.</em></h1>
      <p className="mt-6 max-w-xl text-[#CFC2B6]">The full moon, solstice and seasonal blessings Goddess Ariel has sent you, gathered here to return to whenever you need them.</p>
      <div className="mt-16">
        {!data && !error && <Loader2 className="w-6 h-6 animate-spin text-[#D9B47A]" />}
        {error && <p data-testid="blessings-error" className="text-[#E8838E]">The archive could not be opened just now. Please try again shortly.</p>}
        {data && list.length === 0 && <p data-testid="blessings-empty" className="font-display italic text-2xl text-[#A8998C]">No blessings yet. The first will arrive with the next full moon or turning of the season.</p>}
        {list.map((b, i) => <BlessingCard key={b.id} b={b} name={name} k={list.length - i} signoff={data?.signoff} />)}
      </div>
    </main>
  );
}
